import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Tab, Tabs, Typography } from '@mui/material';
import { Save, Settings } from 'lucide-react';
import {
  GlobalRuntimeSettings,
  useGlobalSettings,
  useUpdateGlobalSettings,
} from '../../services/globalSettings.service';
import { EmptyState } from '../../components/admin/EmptyState';
import { ExchangeRateCard } from '../../components/admin/ExchangeRateCard';
import { HomeAppearanceCard } from '../../components/admin/HomeAppearanceCard';
import { AdminAgentSection } from '../../components/admin/global-settings/AdminAgentSection';
import { AiRuntimeSection } from '../../components/admin/global-settings/AiRuntimeSection';
import { ChatSettingsSection } from '../../components/admin/global-settings/ChatSettingsSection';
import { QuotaBillingSection } from '../../components/admin/global-settings/QuotaBillingSection';
import { isBillingPricingConfigValid } from '../../components/admin/global-settings/billingPricingValidation';
import { useNotification } from '../../hooks/useNotification';
import {
  billingPricingNumberFields,
  fields,
  numberFields,
  type BooleanSettingField,
  type NumberSettingField,
} from './global-settings.config';

/** Settings are grouped by what they affect; every tab edits the same draft and one Save. */
const SETTINGS_GROUPS = [
  { id: 'ai-runtime', label: 'AI runtime' },
  { id: 'chat', label: 'Chat' },
  { id: 'quota-billing', label: 'Quota & billing' },
  { id: 'admin-assistant', label: 'Admin assistant' },
  { id: 'site', label: 'Site' },
] as const;
type SettingsGroupId = (typeof SETTINGS_GROUPS)[number]['id'];

export const GlobalSettings: React.FC = () => {
  const notification = useNotification();
  const { data, isLoading, error } = useGlobalSettings();
  const updateMutation = useUpdateGlobalSettings();
  const [group, setGroup] = useState<SettingsGroupId>('ai-runtime');
  const [config, setConfig] = useState<GlobalRuntimeSettings | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.config) {
      setConfig(data.config);
    }
  }, [data]);

  const hasChanges = useMemo(() => {
    if (!config || !data?.config) return false;
    return fields.some((field) => config[field] !== data.config[field]);
  }, [config, data]);

  const hasInvalidValue =
    config !== null &&
    numberFields.some(
      (field) =>
        !billingPricingNumberFields.includes(field) &&
        (!Number.isInteger(config[field]) || config[field] <= 0)
    );

  const handleNumberChange = (field: NumberSettingField, value: number | undefined) => {
    setConfig((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        [field]: value !== undefined && Number.isFinite(value) ? value : 0,
      };
    });
  };

  const handleBooleanChange = (field: BooleanSettingField, value: boolean) => {
    setConfig((previous) => {
      if (!previous) return previous;

      return {
        ...previous,
        [field]: value,
      };
    });
  };

  const handlePackageIterationChange = (packageCode: string, value: number | undefined) => {
    setConfig((previous) => {
      if (!previous) return previous;
      const next = { ...previous.chatStreamMaxToolIterationsByPackage };
      if (value !== undefined && Number.isFinite(value) && value > 0) {
        next[packageCode] = Math.floor(value);
      } else {
        delete next[packageCode];
      }
      return { ...previous, chatStreamMaxToolIterationsByPackage: next };
    });
  };

  const handleDenyPatternsChange = (patterns: string[]) => {
    setConfig((previous) =>
      previous ? { ...previous, adminAgentExtraDenyPatterns: patterns } : previous
    );
  };

  const handleImageHostsChange = (hosts: string[]) => {
    setConfig((previous) =>
      previous ? { ...previous, generatedImageAllowedHosts: hosts } : previous
    );
  };

  const sectionProps = {
    config: config as GlobalRuntimeSettings,
    onNumberChange: handleNumberChange,
    onBooleanChange: handleBooleanChange,
  };

  const handleSave = async () => {
    if (!config) return;

    try {
      setSaveError(null);
      await updateMutation.mutateAsync(config);
      notification.success('Global settings updated successfully');
    } catch (saveError) {
      const responseMessage =
        typeof saveError === 'object' && saveError !== null && 'response' in saveError
          ? ((saveError as { response?: { data?: { message?: string; error?: string } } }).response
              ?.data?.message ??
            (saveError as { response?: { data?: { error?: string } } }).response?.data?.error)
          : undefined;
      const message =
        responseMessage ??
        (saveError instanceof Error ? saveError.message : 'Failed to update global settings');
      setSaveError(message);
      notification.error(message);
    }
  };

  return (
    <Box className="h-full bg-slate-50 p-8 dark:bg-slate-900">
      <Box className="mb-6 flex items-start justify-between gap-4">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Global Settings
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Runtime behavior for chat, the model gateway, billing and the admin assistant, grouped
            by area.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            updateMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <Save size={20} />
            )
          }
          onClick={handleSave}
          disabled={
            !hasChanges ||
            hasInvalidValue ||
            (config !== null && !isBillingPricingConfigValid(config)) ||
            updateMutation.isPending
          }
          sx={{
            backgroundColor: '#3B82F6',
            '&:hover': {
              backgroundColor: '#2563EB',
            },
            '&:disabled': {
              backgroundColor: '#94a3b8',
            },
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      <Tabs
        value={group}
        onChange={(_, value: SettingsGroupId) => setGroup(value)}
        variant="scrollable"
        scrollButtons="auto"
        className="mb-4"
      >
        {SETTINGS_GROUPS.map((item) => (
          <Tab key={item.id} value={item.id} label={item.label} sx={{ textTransform: 'none' }} />
        ))}
      </Tabs>

      {isLoading ? (
        <Box className="flex h-[400px] items-center justify-center">
          <CircularProgress size={40} className="text-blue-500" />
        </Box>
      ) : error || !config ? (
        <EmptyState
          Icon={Settings}
          title="Failed to load global settings"
          description="Please try again later"
        />
      ) : group === 'site' ? (
        <>
          <ExchangeRateCard />
          <HomeAppearanceCard />
        </>
      ) : (
        <Paper
          elevation={0}
          className="rounded-xl bg-white p-6 dark:bg-slate-800"
          sx={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}
        >
          {hasInvalidValue && (
            <Alert severity="error" className="mb-5">
              Numeric runtime settings must be positive integers.
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" className="mb-5">
              {saveError}
            </Alert>
          )}

          {group === 'ai-runtime' && (
            <AiRuntimeSection {...sectionProps} onImageHostsChange={handleImageHostsChange} />
          )}
          {group === 'chat' && (
            <ChatSettingsSection
              {...sectionProps}
              onPackageIterationChange={handlePackageIterationChange}
            />
          )}
          {group === 'quota-billing' && <QuotaBillingSection {...sectionProps} />}
          {group === 'admin-assistant' && (
            <AdminAgentSection {...sectionProps} onDenyPatternsChange={handleDenyPatternsChange} />
          )}

          <Box className="mt-6 grid grid-cols-1 gap-3 border-t border-gray-200 pt-4 text-sm text-gray-700 dark:border-slate-700 dark:text-slate-300 md:grid-cols-3">
            <Box>Storage: Setting(scope=global)</Box>
            <Box>Runtime read: cache, then D1</Box>
            <Box>Fallback: env var, then code default</Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default GlobalSettings;
